/**
 * /earthworks/sites — the customer's sites (GET /earthworks/sites, EW-1).
 *
 * Full-width register; a row opens the site on its own page. Filters live in
 * the URL (kind, customer, status, search) so Back returns to the list as it
 * was left, and filter server-side. The counts are a tally of the unfiltered
 * list (no endpoint returns one).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, MapPin, Plus, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useEwMeta, useEwSites } from '../../hooks/useEarthworks';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminEw, ewStatusSkin } from '../../lib/earthworks';
import type { EwSite } from '../../types/earthworks';
import { useOpenRecord } from '../downhole/recordPage';
import { EW_EYEBROW, EW_INPUT, EwBadge, EwChip, EwChipRow, EwEmpty, EwPanel, EwRegister, type EwColumn } from './EwBits';

export default function EwSitesScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = searchParams.get('kind') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const status = searchParams.get('status') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const catalog = useEwMeta().data?.data ?? null;
  const filters = useMemo(() => ({ kind, customerId, status, q: qParam }), [kind, customerId, status, qParam]);
  const filtered = !!(kind || customerId || status || qParam);
  const listQuery = useEwSites(filters);
  const allQuery = useEwSites();
  const sites = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (s: EwSite) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canAdminEw(user?.role);

  const customers = useMemo(() => {
    const seen = new Map<string, string>();
    (all ?? []).forEach((s) => seen.set(s.customerId, s.customerName ?? '—'));
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);

  const kindLabel = (s: EwSite) => s.kindLabel ?? catalog?.siteKinds.find((k) => k.value === s.kind)?.label ?? s.kind ?? '—';
  const where = (s: EwSite) => [s.address, s.city].filter(Boolean).join(', ') || s.legalLand || '—';

  const columns: EwColumn<EwSite>[] = [
    {
      key: 'name',
      header: 'Site',
      render: (s) => (
        <>
          <p className="font-extrabold text-white">{s.name || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{kindLabel(s)}</p>
        </>
      ),
    },
    { key: 'customer', header: 'Customer', render: (s) => s.customerName || '—' },
    { key: 'where', header: 'Address / location', render: (s) => where(s) },
    { key: 'province', header: 'Province / state', render: (s) => s.provinceState || '—' },
    { key: 'jobs', header: 'Jobs', align: 'right', render: (s) => displayValue(s.jobCount ?? undefined) },
    { key: 'status', header: 'Status', render: (s) => <EwBadge skin={ewStatusSkin(s.status)} value={s.status} /> },
  ];

  return (
    <div data-testid="ew-sites-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title="Sites"
        description="Lots, commercial sites and leases your crews work on."
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/earthworks/sites/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New site
            </button>
          ) : undefined
        }
        stats={[
          { icon: <MapPin className="size-4" aria-hidden="true" />, label: 'Sites', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <MapPin className="size-4" aria-hidden="true" />, label: 'Active', tone: 'success', loading: countsLoading, value: countOf((s) => s.status === 'active') },
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'With jobs', tone: 'info', loading: countsLoading, value: countOf((s) => (s.jobCount ?? 0) > 0) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <EwChipRow label="Filter by kind">
            <EwChip label="All" active={!kind} onClick={() => setParam('kind', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {(catalog?.siteKinds ?? []).map((k) => (
              <EwChip
                key={k.value}
                label={k.label}
                active={kind === k.value}
                onClick={() => setParam('kind', k.value)}
                count={countsLoading ? undefined : countOf((s) => s.kind === k.value)}
              />
            ))}
          </EwChipRow>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_14rem_12rem]">
            <label className="relative block">
              <span className="sr-only">Search name, address, city, legal land or customer</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, address, city, legal land or customer"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select className={EW_INPUT} aria-label="Filter by customer" value={customerId} onChange={(e) => setParam('customerId', e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select className={EW_INPUT} aria-label="Filter by status" value={status} onChange={(e) => setParam('status', e.target.value)}>
              <option value="">Any status</option>
              {(catalog?.siteStatuses ?? []).map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <EwPanel
            eyebrow="Site register"
            title="Earthworks sites"
            testId="ew-site-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && sites.length > 0
                ? filtered
                  ? `Showing ${sites.length} of ${countOf(() => true)} sites`
                  : `${sites.length} site${sites.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <EwRegister
              query={listQuery}
              rows={sites}
              columns={columns}
              rail={(s) => ewStatusSkin(s.status).rail}
              onOpen={(s) => openRecord(`/earthworks/sites/${s.id}`)}
              subject="sites"
              testId="ew-site"
              minWidth="min-w-[960px]"
              empty={filtered ? <EwEmpty>No site matches these filters.</EwEmpty> : <EwEmpty>There are no earthworks sites yet.</EwEmpty>}
              card={(s) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-white">{s.name || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {s.customerName || '—'} · {kindLabel(s)}
                      </p>
                    </div>
                    <EwBadge skin={ewStatusSkin(s.status)} value={s.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {where(s)} · Jobs {displayValue(s.jobCount ?? undefined)}
                  </p>
                </>
              )}
            />
          </EwPanel>
        </div>
      </section>
    </div>
  );
}
